
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { TenantVideoModel } from 'src/app/models/tenants/tenant-video.model';
import { CourseService } from 'src/app/services/course.service';
import { LoadingService } from 'src/app/services/loading.service';
import { TenantVideoService } from 'src/app/services/tenants/tenant-video.service';
import { VideoPlayerComponent } from '../shared/video-player/video-player.component';

@Component({
  selector: 'app-tenant-video',
  standalone: false,
  templateUrl: './tenant-video.component.html',
  styleUrl: './tenant-video.component.scss',
})
export class TenantVideoComponent implements OnInit, OnDestroy {
  @Input()
  tenantVideo: TenantVideoModel | null = null;

  @Output()
  completed = new EventEmitter<void>();

  private isProgressLoaded = false;
  private videoPlayer?: VideoPlayerComponent;

  @ViewChild('videoPlayer')
  set videoPlayerRef(player: VideoPlayerComponent | undefined) {
    if (player) {
      this.videoPlayer = player;
      this.initializeVideoPlayer();
    }
  }

  private progressSubscription?: Subscription;

  isVisible = false;
  isLoading = true;
  isCompleting = false;
  videoProgressSeconds = 0;
  errorMessage: string | null = null;

  constructor(
    private tenantVideoService: TenantVideoService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private courseService: CourseService,
  ) {}

  ngOnInit(): void {
    if (!this.tenantVideo) {
      this.isLoading = false;
      return;
    }

    if (!this.tenantVideo.file.hlsUrl && !this.tenantVideo.file.fileUrl) {
      this.isLoading = false;
      this.errorMessage = this.translateService.instant(
        'TENANT-VIDEO.OVERVIEW.PLAYBACK-ERROR'
      );
      return;
    }

    this.loadVideoProgress(this.tenantVideo.eventId);
  }

  private initializeVideoPlayer(): void {
    if (!this.isProgressLoaded || !this.videoPlayer) {
      return;
    }

    this.videoPlayer.playVideo();
  }

  private loadVideoProgress(eventId: string): void {
    this.loadingService.load(
      this.courseService.getVideoProgress(eventId)
    ).subscribe({
      next: progress => {
        this.videoProgressSeconds = progress.progressSeconds ?? 0;
        this.isVisible = true;
        this.isLoading = false;
        this.isProgressLoaded = true;

        this.initializeVideoPlayer();
        this.startProgressTracking();
      },
      error: () => {
        this.videoProgressSeconds = 0;
        this.isVisible = true;
        this.isLoading = false;
        this.startProgressTracking();
      }
    });
  }

  private startProgressTracking(): void {
    this.progressSubscription = timer(2500, 2500)
      .subscribe(() => {
        this.videoPlayer?.updatePlayTime();
      });
  }

  onVideoProgressChanged(seconds: number): void {
    this.videoProgressSeconds = seconds;

    if (this.tenantVideo?.eventId) {
      this.courseService.trackVideoProgress(
        this.tenantVideo.eventId,
        seconds
      ).subscribe();
    }
  }

  onVideoEnded(): void {
    if (this.isCompleting) {
      return;
    }

    this.progressSubscription?.unsubscribe();
    this.isCompleting = true;
    this.errorMessage = null;

    this.loadingService.load(
      this.tenantVideoService.completeTenantVideo()
    ).subscribe({
      next: () => {
        this.isVisible = false;
        this.tenantVideo = null;
        this.completed.emit();
      },
      error: () => {
        this.isCompleting = false;
        this.errorMessage = this.translateService.instant(
          'TENANT-VIDEO.OVERVIEW.COMPLETION-ERROR'
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.progressSubscription?.unsubscribe();
  }
}
